import React, { useState } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Button,
  Drawer,
  TextField,
  Stack,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Fab,
  TablePagination,
  CircularProgress,
  Snackbar,
  Alert,
  Checkbox,
} from '@mui/material'
import {
  FilterList as FilterIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import useInvoiceRecords from '../../../hooks/useInvoiceRecords'
import usePermission from '../../../hooks/usePermission'
import RemarkModal from './RemarkModal'
import InvoiceActionsModal from './InvoiceActionsModal'
import SolarPlantIcon from '../../../assets/images/solar plant_3.svg'

const InvoiceRecordsMobileView = () => {
  const {
    invoices,
    loading,
    filters,
    setFilters,
    selectedInvoices,
    page,
    rowsPerPage,
    snackbar,
    plantNames,
    organizations,
    statusOptions,
    handleSelectInvoice,
    handleUpdateInvoice,
    handleSendEmail,
    handleExportToZoho,
    closeSnackbar,
    handleChangePage,
    handleChangeRowsPerPage,
    setSelectedInvoices,
  } = useInvoiceRecords()

  usePermission()
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [filterMode, setFilterMode] = useState('date')
  const [remarkModalOpen, setRemarkModalOpen] = useState(false)
  const [actionsModalOpen, setActionsModalOpen] = useState(false)
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null)

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value })
  }

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'PAID':
        return 'success'
      case 'PENDING':
        return 'warning'
      case 'UNPAID':
        return 'error'
      default:
        return 'default'
    }
  }

  const getCardBorderColor = (status) => {
    if (status?.toUpperCase() === 'PAID') return '#4CAF50'
    if (status?.toUpperCase() === 'UNPAID') return '#f44336'
    return 'transparent'
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0)
  }

  const handleViewRemark = (invoice) => {
    setSelectedInvoiceId(invoice._id)
    setRemarkModalOpen(true)
  }

  const handleOpenActionsModal = (invoice) => {
    if (!selectedInvoices.includes(invoice._id)) {
      setSelectedInvoices([invoice._id])
    }
    setActionsModalOpen(true)
  }

  const handleCloseRemarkModal = () => {
    setRemarkModalOpen(false)
    setSelectedInvoiceId(null)
  }

  const handleCloseActionsModal = () => {
    setActionsModalOpen(false)
  }

  const getSelectedInvoiceObjects = () => {
    return invoices.filter((inv) => selectedInvoices.includes(inv._id))
  }

  // Filter Drawer Content
  const FilterDrawer = () => (
    <Box sx={{ p: 2, width: '85vw', maxWidth: 350 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Filters</Typography>
        <IconButton onClick={() => setFilterDrawerOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2}>
        <Box display="flex" justifyContent="center" mb={1}>
          <ToggleButtonGroup
            value={filterMode}
            exclusive
            onChange={(e, val) => val && setFilterMode(val)}
            size="small"
          >
            <ToggleButton value="date">
              <CalendarIcon fontSize="small" sx={{ mr: 1 }} /> By Date
            </ToggleButton>
            <ToggleButton value="plant">
              <img
                src={SolarPlantIcon}
                alt="plant"
                style={{ width: 18, height: 18, marginRight: 8 }}
              />{' '}
              By Plant
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {filterMode === 'plant' && (
          <>
            <Autocomplete
              size="small"
              options={plantNames}
              value={filters.plantName || ''}
              onChange={(event, newValue) => handleFilterChange('plantName', newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Plant" placeholder="Plant Name" />
              )}
            />
            <Autocomplete
              size="small"
              options={organizations}
              value={filters.organization || ''}
              onChange={(event, newValue) =>
                handleFilterChange('organization', newValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Organization"
                  placeholder="Organization"
                />
              )}
            />
            <Autocomplete
              size="small"
              options={statusOptions}
              value={filters.status || ''}
              onChange={(event, newValue) => handleFilterChange('status', newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Select Status" placeholder="Status" />
              )}
            />
          </>
        )}

        {filterMode === 'date' && (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Month"
              views={['year', 'month']}
              value={filters.period}
              onChange={(newValue) => handleFilterChange('period', newValue)}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>
        )}

        <Divider />

        <Button
          variant="outlined"
          fullWidth
          onClick={() => {
            setFilters({
              plantName: '',
              organization: '',
              status: '',
              period: null,
            })
          }}
        >
          Clear All Filters
        </Button>

        <Button
          variant="contained"
          fullWidth
          onClick={() => setFilterDrawerOpen(false)}
        >
          Apply Filters
        </Button>
      </Stack>
    </Box>
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ pb: 10, px: 2, pt: 2, minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Invoice Records
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {invoices.length} invoice(s) found
          </Typography>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        )}

        {/* Invoice Cards */}
        {!loading && (
          <>
            {invoices
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((invoice) => {
                const isUnpaid =
                  invoice.status?.toUpperCase() === 'UNPAID' ||
                  invoice.paymentStatus?.toUpperCase() === 'UNPAID'
                const isSelected = selectedInvoices.includes(invoice._id)

                return (
                  <Card
                    key={invoice._id}
                    sx={{
                      mb: 2,
                      borderLeft: '4px solid',
                      borderLeftColor: getCardBorderColor(
                        invoice.status || invoice.paymentStatus
                      ),
                      backgroundColor: isUnpaid ? 'rgba(255, 0, 0, 0.05)' : 'white',
                    }}
                  >
                    <CardContent>
                      {/* Header with Checkbox */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        mb={1}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectInvoice(invoice._id)}
                            size="small"
                          />
                          <Typography variant="subtitle1" fontWeight={600}>
                            {invoice.plantName || 'N/A'}
                          </Typography>
                        </Box>
                        <Chip
                          label={invoice.status || invoice.paymentStatus || 'Unknown'}
                          color={getStatusColor(invoice.status || invoice.paymentStatus)}
                          size="small"
                        />
                      </Box>

                      {/* Invoice Details */}
                      <Stack spacing={1} sx={{ mb: 2 }}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Invoice Number:
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {invoice.invoiceNumber || 'N/A'}
                          </Typography>
                        </Box>

                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Organization:
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {invoice.organizationId || 'N/A'}
                          </Typography>
                        </Box>

                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Invoice Date:
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {invoice.invoiceDate
                              ? dayjs(invoice.invoiceDate).format('DD MMM YYYY')
                              : 'N/A'}
                          </Typography>
                        </Box>

                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Due Date:
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {invoice.dueDate
                              ? dayjs(invoice.dueDate).format('DD MMM YYYY')
                              : 'N/A'}
                          </Typography>
                        </Box>

                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Amount:
                          </Typography>
                          <Typography variant="body1" fontWeight={700} color="primary">
                            ₹{formatCurrency(invoice.invoiceValue)}
                          </Typography>
                        </Box>
                      </Stack>

                      {/* Action Buttons */}
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewRemark(invoice)}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenActionsModal(invoice)}
                        >
                          Action
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )
              })}

            {invoices.length === 0 && !loading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="300px"
              >
                <Typography variant="body1" color="text.secondary">
                  No invoices found
                </Typography>
              </Box>
            )}

            {/* Pagination */}
            {invoices.length > 0 && (
              <TablePagination
                component="div"
                count={invoices.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            )}
          </>
        )}

        {/* Filter FAB */}
        <Fab
          color="primary"
          aria-label="filter"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 16,
          }}
          onClick={() => setFilterDrawerOpen(true)}
        >
          <FilterIcon />
        </Fab>

        {/* Bulk Actions FAB */}
        {selectedInvoices.length > 0 && (
          <Fab
            color="secondary"
            aria-label="actions"
            variant="extended"
            sx={{
              position: 'fixed',
              bottom: 150,
              right: 16,
            }}
            onClick={() => setActionsModalOpen(true)}
          >
            <EditIcon sx={{ mr: 1 }} />
            Actions ({selectedInvoices.length})
          </Fab>
        )}

        {/* Filter Drawer */}
        <Drawer
          anchor="right"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
        >
          <FilterDrawer />
        </Drawer>

        {/* Modals */}
        <RemarkModal
          open={remarkModalOpen}
          onClose={handleCloseRemarkModal}
          invoiceId={selectedInvoiceId}
        />

        <InvoiceActionsModal
          open={actionsModalOpen}
          onClose={handleCloseActionsModal}
          selectedInvoices={getSelectedInvoiceObjects()}
          period={filters.period}
          onUpdate={handleUpdateInvoice}
          onSendEmail={handleSendEmail}
          onExportToZoho={handleExportToZoho}
        />

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={closeSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  )
}

export default InvoiceRecordsMobileView
